import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useOfflineSync } from "../../context/OfflineSyncProvider";

const INITIAL_ALERT_DURATION = 4000;

const BannerContainer = styled("div")(({ theme }) => ({
  position: "fixed",
  top: "calc(var(--ion-safe-area-top, 0px) + var(--appbar-offset, 0px) + 12px)",
  left: 0,
  right: 0,
  display: "flex",
  justifyContent: "center",
  zIndex: theme.zIndex.snackbar + 10,
  padding: theme.spacing(0.5, 1),
  backgroundColor: "transparent",
  pointerEvents: "none",
  "& > *": {
    pointerEvents: "auto",
  },
}));

const OfflineStatusBanner: React.FC = () => {
  const {
    featureEnabled,
    isReady,
    isOnline,
    isForcedOffline,
    setForcedOffline,
    queueCounts,
    queueState,
    pausedReason,
    syncNow,
  } = useOfflineSync();

  const status = useMemo(() => {
    if (!featureEnabled || !isReady) {
      return null;
    }

    const entityMetadata = queueState?.entityMetadata ?? {};
    const hasQueue = queueCounts.total > 0;
    const hasErrors = Object.values(entityMetadata).some(
      (meta) => meta.status === "error"
    );
    const isPaused = queueState?.paused ?? false;

    if (!isOnline || isForcedOffline) {
      return {
        severity: "warning" as const,
        icon: <CloudOffIcon fontSize="small" />,
        message: isForcedOffline
          ? "Forced offline mode is active. Changes will queue until you go back online."
          : "Offline detected. Changes will sync once the connection is restored.",
        showSync: hasQueue,
      };
    }

    if (isPaused) {
      return {
        severity: "error" as const,
        icon: <WarningAmberIcon fontSize="small" />,
        message:
          pausedReason ??
          "Sync paused due to authentication issues. Sign back in and try again.",
        showSync: true,
      };
    }

    if (hasQueue) {
      return {
        severity: hasErrors ? ("error" as const) : ("info" as const),
        icon: <CloudQueueIcon fontSize="small" />,
        message: hasErrors
          ? "Some updates failed to sync. Review the queue and retry."
          : "Updates queued. We'll sync them automatically when possible.",
        showSync: true,
      };
    }

    return null;
  }, [
    featureEnabled,
    isForcedOffline,
    isOnline,
    isReady,
    pausedReason,
    queueCounts.error,
    queueCounts.total,
    queueState?.entityMetadata,
    queueState?.paused,
  ]);

  const statusKey = status
    ? `${status.severity}|${status.message}|${status.showSync ? "1" : "0"}|${isForcedOffline ? "1" : "0"}`
    : null;

  const [mode, setMode] = useState<"alert" | "banner">("alert");

  useEffect(() => {
    if (!statusKey) {
      setMode("alert");
      return;
    }
    setMode("alert");
    const timer = window.setTimeout(() => setMode("banner"), INITIAL_ALERT_DURATION);
    return () => window.clearTimeout(timer);
  }, [statusKey]);

  if (!status) {
    return null;
  }

  const actions = (
    <Stack direction="row" spacing={1}>
      <Button
        size="small"
        variant="outlined"
        onClick={() => void setForcedOffline(!isForcedOffline)}
      >
        {isForcedOffline ? "Go Online" : "Go Offline"}
      </Button>
      {status.showSync && (
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={() => void syncNow()}
        >
          Sync Now
        </Button>
      )}
    </Stack>
  );

  const alertContent = (
    <Alert
      severity={status.severity}
      icon={status.icon}
      action={actions}
      sx={{ alignItems: "center" }}
      variant={mode === "banner" ? "outlined" : undefined}
    >
      <Typography variant="body2">{status.message}</Typography>
    </Alert>
  );

  if (mode === "alert") {
    return <BannerContainer>{alertContent}</BannerContainer>;
  }

  return null;
};

export default OfflineStatusBanner;
